import { useEffect } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

/**
 * Prevents right-click context menu
 * @param {Event} event - The event object
 */
export const preventContextMenu = (event) => {
    event.preventDefault();
    return false;
};

/**
 * Prevents keyboard shortcuts
 * @param {Event} event - The event object
 */
export const preventKeyboardShortcuts = (event) => {
    // Prevent common keyboard shortcuts
    if (
        (event.ctrlKey || event.metaKey) && (
            event.key === 'c' || // Copy
            event.key === 'v' || // Paste
            event.key === 'u' || // View source
            event.key === 's' || // Save
            event.key === 'p' || // Print
            event.key === 'a' || // Select all
            event.key === 'z' || // Undo
            event.key === 'y' || // Redo
            event.key === 'f' || // Find
            event.key === 'r' || // Reload
            event.key === 'n' || // New window
            event.key === 'w' || // Close window
            event.key === 't' || // New tab
            event.key === 'l'    // Location bar
        )
    ) {
        event.preventDefault();
        event.stopPropagation();
        return false;
    }

    // Prevent screenshot shortcuts
    if (
        // Windows: PrintScreen, Alt+PrintScreen, Win+PrintScreen
        event.key === 'PrintScreen' ||
        (event.altKey && event.key === 'PrintScreen') ||
        (event.metaKey && event.key === 'PrintScreen') ||
        
        // macOS: Cmd+Shift+3, Cmd+Shift+4, Cmd+Shift+5
        (event.metaKey && event.shiftKey && (
            event.key === '3' ||
            event.key === '4' ||
            event.key === '5'
        )) ||
        
        // Linux: PrintScreen, Alt+PrintScreen
        (event.altKey && event.key === 'PrintScreen') ||
        
        // Additional screenshot combinations
        (event.ctrlKey && event.key === 'PrintScreen') ||
        (event.shiftKey && event.key === 'PrintScreen')
    ) {
        event.preventDefault();
        return false;
    }

    // Prevent F12, Ctrl+Shift+I, Ctrl+Shift+J, Ctrl+Shift+C
    if (
        event.key === 'F12' ||
        (event.ctrlKey && event.shiftKey && (
            event.key === 'I' ||
            event.key === 'i' ||
            event.key === 'J' ||
            event.key === 'j' ||
            event.key === 'C' ||
            event.key === 'c'
        ))
    ) {
        event.preventDefault();
        return false;
    }
    
    if(event.key === 'F11' || event.key === 'Esc')
    {
        event.preventDefault();
        return false;
    }

    // منع مفاتيح Alt
    if (event.altKey) {
        event.preventDefault();
        return false;
    }

    // F5
    if (event.key === 'F5') {
        event.preventDefault();
        return false;
    }
    // Ctrl+R or Cmd+R
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'r') {
        event.preventDefault();
        return false;
    }
    // Ctrl+Shift+R
    if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key.toLowerCase() === 'r') {
        event.preventDefault();
        return false;
    }

    return true;
};

/**
 * Prevents text selection
 * @param {Event} event - The event object
 */
export const preventTextSelection = (event) => {
    event.preventDefault();
    return false;
};



/**
 * Enhanced tab change detection
 * @param {Function} onTabChange - Callback function when tab change is detected
 * @returns {Function} Cleanup function
 */
export const setupTabChangeDetection = (onTabChange) => {
    let isPageVisible = true;
    let lastFocusTime = Date.now();
    let tabChangeTimeout = null;

    const handleVisibilityChange = () => {
        if (document.hidden && isPageVisible) {
            isPageVisible = false;
            if (onTabChange) {
                onTabChange('visibility');
            }
        } else if (!document.hidden && !isPageVisible) {
            isPageVisible = true;
        }
    };

    const handleWindowBlur = () => {
        if (isPageVisible) {
            isPageVisible = false;
            const timeDiff = Date.now() - lastFocusTime;
            if (timeDiff < 1000) { // تغيير سريع لعلامة التبويب
                if (onTabChange) {
                    onTabChange('blur');
                }
            }
        }
    };

    const handleWindowFocus = () => {
        isPageVisible = true;
        lastFocusTime = Date.now();
    };

    // إضافة مستمعي الأحداث
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleWindowBlur);
    window.addEventListener('focus', handleWindowFocus);

    // Return cleanup function
    return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        window.removeEventListener('blur', handleWindowBlur);
        window.removeEventListener('focus', handleWindowFocus);
        if (tabChangeTimeout) {
            clearTimeout(tabChangeTimeout);
        }
    };
};

/**
 * React Hook for setting up exam security measures
 * @param {boolean} [enableContextMenu=false] - Whether to allow the context menu
 */
export const useExamSecurity = (enableContextMenu = false) => {
    // List of keys to block
    const blockedKeys = [
        // Common navigation/action shortcuts
        'ctrl+c', 'meta+c',
        'ctrl+v', 'meta+v',
        'ctrl+u', 'meta+u',
        'ctrl+s', 'meta+s',
        'ctrl+p', 'meta+p',
        'ctrl+a', 'meta+a',
        'ctrl+z', 'meta+z',
        'ctrl+y', 'meta+y',
        'ctrl+f', 'meta+f',
        'ctrl+r', 'meta+r',
        'ctrl+n', 'meta+n',
        'ctrl+w', 'meta+w',
        'ctrl+t', 'meta+t',
        'ctrl+l', 'meta+l',
        'ctrl+shift+r', 'meta+shift+r',
        
        // PrintScreen / Screenshot keys
        'printscreen',
        'alt+printscreen',
        'meta+printscreen',
        'ctrl+printscreen',
        'shift+printscreen',
        'meta+shift+3',
        'meta+shift+4',
        'meta+shift+5',
        
        // Developer tools
        'f12',
        'ctrl+shift+i',
        'ctrl+shift+j',
        'ctrl+shift+c',
        
        // Extra function keys & window actions
        'f11',
        'esc',
        'alt',
        'f5'
    ].join(', ');

    // Block keyboard shortcuts declaratively
    useHotkeys(blockedKeys, (event) => {
        event.preventDefault();
        event.stopPropagation();
    }, {
        enableOnFormTags: true,
        preventDefault: true
    });

    useEffect(() => {
        // Prevent right-click context menu
        const handleContextMenu = (event) => {
            if (!enableContextMenu) {
                event.preventDefault();
                return false;
            }
        };
        document.addEventListener('contextmenu', handleContextMenu);
        
        // Prevent text selection and dragging
        const handleSelectStart = (event) => {
            event.preventDefault();
            return false;
        };
        document.addEventListener('selectstart', handleSelectStart);
        document.addEventListener('dragstart', handleSelectStart);
        
        // Prevent copy, paste, cut
        const handleCopyCutPaste = (e) => e.preventDefault();
        document.addEventListener('copy', handleCopyCutPaste);
        document.addEventListener('paste', handleCopyCutPaste);
        document.addEventListener('cut', handleCopyCutPaste);
        
        // Prevent drag & drop
        const preventDragDrop = (e) => {
            e.preventDefault();
            e.stopPropagation();
            return false;
        };
        document.addEventListener('drop', preventDragDrop);
        document.addEventListener('dragover', preventDragDrop);
        window.addEventListener('drop', preventDragDrop);
        window.addEventListener('dragover', preventDragDrop);
        
        // Prevent tab close/reload confirmation
        const preventUnload = (e) => {
            e.preventDefault();
            e.returnValue = '';
            return '';
        };
        window.addEventListener('beforeunload', preventUnload);
        
        // Prevent print
        const preventPrint = (e) => {
            e.preventDefault();
            return false;
        };
        window.addEventListener('beforeprint', preventPrint);
        window.addEventListener('afterprint', preventPrint);
        
        return () => {
            document.removeEventListener('contextmenu', handleContextMenu);
            document.removeEventListener('selectstart', handleSelectStart);
            document.removeEventListener('dragstart', handleSelectStart);
            document.removeEventListener('copy', handleCopyCutPaste);
            document.removeEventListener('paste', handleCopyCutPaste);
            document.removeEventListener('cut', handleCopyCutPaste);
            document.removeEventListener('drop', preventDragDrop);
            document.removeEventListener('dragover', preventDragDrop);
            window.removeEventListener('drop', preventDragDrop);
            window.removeEventListener('dragover', preventDragDrop);
            window.removeEventListener('beforeunload', preventUnload);
            window.removeEventListener('beforeprint', preventPrint);
            window.removeEventListener('afterprint', preventPrint);
        };
    }, [enableContextMenu]);
};

/**
 * Legacy security setup function for backward compatibility
 * @param {boolean} [enableContextMenu=false] - Whether to allow the context menu
 * @returns {Function} Cleanup function to remove event listeners
 */
export const setupExamSecurity = (enableContextMenu = false) => {
    if (!enableContextMenu) {
        document.addEventListener('contextmenu', preventContextMenu);
    }
    document.addEventListener('keydown', preventKeyboardShortcuts);
    document.addEventListener('selectstart', preventTextSelection);
    document.addEventListener('dragstart', preventTextSelection);
    
    const preventPrint = (event) => {
        if ((event.ctrlKey || event.metaKey) && event.key === 'p') {
            event.preventDefault();
            event.stopPropagation();
            return false;
        }
    };
    document.addEventListener('keydown', preventPrint);
    window.addEventListener('keydown', preventPrint);

    document.addEventListener('copy', (e) => e.preventDefault());
    document.addEventListener('paste', (e) => e.preventDefault());
    document.addEventListener('cut', (e) => e.preventDefault());
    
    const preventDragDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        return false;
    };
    document.addEventListener('drop', preventDragDrop);
    document.addEventListener('dragover', preventDragDrop);
    window.addEventListener('drop', preventDragDrop);
    window.addEventListener('dragover', preventDragDrop);
    
    window.addEventListener('beforeunload', (e) => {
        e.preventDefault();
        e.returnValue = '';
        return '';
    });
    
    window.addEventListener('beforeprint', (e) => {
        e.preventDefault();
        return false;
    });
    
    window.addEventListener('afterprint', (e) => {
        e.preventDefault();
        return false;
    });
    
    return () => {
        if (!enableContextMenu) {
            document.removeEventListener('contextmenu', preventContextMenu);
        }
        document.removeEventListener('keydown', preventKeyboardShortcuts);
        document.removeEventListener('selectstart', preventTextSelection);
        document.removeEventListener('dragstart', preventTextSelection);
        document.removeEventListener('copy', (e) => e.preventDefault());
        document.removeEventListener('paste', (e) => e.preventDefault());
        document.removeEventListener('cut', (e) => e.preventDefault());
        document.removeEventListener('drop', preventDragDrop);
        document.removeEventListener('dragover', preventDragDrop);
        window.removeEventListener('drop', preventDragDrop);
        window.removeEventListener('dragover', preventDragDrop);
        window.removeEventListener('beforeunload', (e) => {
            e.preventDefault();
            e.returnValue = '';
            return '';
        });
        document.removeEventListener('keydown', preventPrint);
        window.removeEventListener('keydown', preventPrint);
        window.removeEventListener('beforeprint', (e) => {
            e.preventDefault();
            return false;
        });
        window.removeEventListener('afterprint', (e) => {
            e.preventDefault();
            return false;
        });
    };
}; 